import React, { useState, useEffect, useRef, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { Box, IconButton } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import UserCampaignStatsRow from "../../../constants/SubTables/userCampaignStats";
import { leftScrollButton, rightScrollButton, scrollIcon, wrapperBox } from "./styles";

const ROW_SELECT_COLUMN_ID = "mrt-row-select";

const buildColumnOrder = (order, columnPinning, selectable) => {
  const safeOrder = Array.isArray(order) ? [...order] : [];

  if (selectable && !safeOrder.includes(ROW_SELECT_COLUMN_ID)) {
    safeOrder.unshift(ROW_SELECT_COLUMN_ID);
  }

  const leftPinned = [
    ...(selectable ? [ROW_SELECT_COLUMN_ID] : []),
    ...(columnPinning?.left || []).filter(
      (columnId) => columnId !== ROW_SELECT_COLUMN_ID
    ),
  ];
  const rightPinned = columnPinning?.right || [];

  const middle = safeOrder.filter(
    (columnId) =>
      !leftPinned.includes(columnId) && !rightPinned.includes(columnId)
  );

  return [...leftPinned, ...middle, ...rightPinned];
};

const DataTable = ({
  columns,
  data,
  selectable = false,
  setSorting,
  totalData,
  sorting,
  pagination,
  setLoader,
  showLoaderOnSort = true,
  manualPagination = true,
  setPagination,
  rowSelection = {},
  enableCollapsibleRow = true,
  setRowSelection,
  density,
  columnVisibility,
  setColumnVisibility,
  columnPinning,
  columnOrder,
  setColumnOrder,
}) => {
  const onPaginationChange = (rows) => {
    setLoader(true);
    setPagination(rows);
  };

  const onSortingChange = (rows) => {
    if (showLoaderOnSort && setLoader) {
      setLoader(true);
    }
    setSorting(rows);
  };

  const resolvedColumnPinning = useMemo(() => {
    if (!selectable && !columnPinning) {
      return columnPinning;
    }

    return {
      left: [
        ...(selectable ? [ROW_SELECT_COLUMN_ID] : []),
        ...(columnPinning?.left || []).filter(
          (columnId) => columnId !== ROW_SELECT_COLUMN_ID
        ),
      ],
      right: columnPinning?.right || [],
    };
  }, [selectable, columnPinning]);

  const defaultDataColumnOrder = useMemo(() => {
    return columns.map((column) => column.id || column.accessorKey).filter(Boolean);
  }, [columns]);

  const effectiveColumnOrder = useMemo(() => {
    if (!setColumnOrder) {
      return undefined;
    }

    const baseOrder = columnOrder?.length ? columnOrder : defaultDataColumnOrder;

    return buildColumnOrder(baseOrder, resolvedColumnPinning, selectable);
  }, [
    setColumnOrder,
    columnOrder,
    defaultDataColumnOrder,
    resolvedColumnPinning,
    selectable,
  ]);

  const isColumnOrderLocked = (column) =>
    column.id === ROW_SELECT_COLUMN_ID ||
    column.columnDef?.enableColumnOrdering === false;

  const handleColumnOrderChange = (updater) => {
    if (!setColumnOrder) return;

    setColumnOrder((previousOrder) => {
      const baseOrder = buildColumnOrder(
        previousOrder?.length ? previousOrder : defaultDataColumnOrder,
        resolvedColumnPinning,
        selectable
      );
      const nextOrder =
        typeof updater === "function" ? updater(baseOrder) : updater;

      return buildColumnOrder(nextOrder, resolvedColumnPinning, selectable);
    });
  };

  const tableContainerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [canScroll, setCanScroll] = useState(false);

  useEffect(() => {
    const checkScrollability = () => {
      if (!wrapperRef.current) return;

      const findScrollableElement = (element) => {
        if (!element) return null;
        
        const style = window.getComputedStyle(element);
        if (style.overflowX === 'auto' || style.overflowX === 'scroll') {
          if (element.scrollWidth > element.clientWidth) {
            return element;
          }
        }
        
        const children = Array.from(element.children);
        for (const child of children) {
          const found = findScrollableElement(child);
          if (found) return found;
        }
        return null;
      };

      let attempts = 0;
      let scrollableElementRef = null;
      let cleanupFn = null;

      const tryFind = () => {
        const scrollableElement = findScrollableElement(wrapperRef.current);
        
        if (scrollableElement) {
          scrollableElementRef = scrollableElement;
          const hasHorizontalScroll = scrollableElement.scrollWidth > scrollableElement.clientWidth;
          setCanScroll(hasHorizontalScroll);

          if (hasHorizontalScroll) {
            const updateArrowVisibility = () => {
              const scrollLeft = scrollableElement.scrollLeft;
              const scrollWidth = scrollableElement.scrollWidth;
              const clientWidth = scrollableElement.clientWidth;

              setShowLeftArrow(scrollLeft > 5); 
              setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
            };

            
            updateArrowVisibility();
            
            if (scrollableElement.scrollLeft === 0) {
              setShowRightArrow(true);
            }
            
            scrollableElement.addEventListener("scroll", updateArrowVisibility);

            const resizeObserver = new ResizeObserver(() => {
              setTimeout(updateArrowVisibility, 50);
            });
            resizeObserver.observe(scrollableElement);

            cleanupFn = () => {
              scrollableElement.removeEventListener("scroll", updateArrowVisibility);
              resizeObserver.disconnect();
            };
          } else {
            setShowLeftArrow(false);
            setShowRightArrow(false);
          }
        } else if (attempts < 5) {
          attempts++;
          setTimeout(tryFind, 200);
        }
      };

      tryFind();

      return () => {
        if (cleanupFn) {
          cleanupFn();
        }
      };
    };

    const timeoutId = setTimeout(checkScrollability, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [data, columns, columnVisibility]);

  const handleScroll = (direction) => {
    if (!wrapperRef.current) return;

    const findScrollableElement = (element) => {
      if (!element) return null;
      
      const style = window.getComputedStyle(element);
      if (style.overflowX === 'auto' || style.overflowX === 'scroll') {
        if (element.scrollWidth > element.clientWidth) {
          return element;
        }
      }
      
      const children = Array.from(element.children);
      for (const child of children) {
        const found = findScrollableElement(child);
        if (found) return found;
      }
      return null;
    };

    const scrollableElement = findScrollableElement(wrapperRef.current);
    if (!scrollableElement) return;

    const scrollAmount = 300;
    const currentScroll = scrollableElement.scrollLeft;
    const targetScroll =
      direction === "left"
        ? Math.max(0, currentScroll - scrollAmount)
        : currentScroll + scrollAmount;

    scrollableElement.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  };

  return (
    <Box ref={wrapperRef} sx={wrapperBox}>
      {canScroll && showLeftArrow && (
        <IconButton onClick={() => handleScroll("left")} sx={leftScrollButton}>
          <ArrowBackIosIcon sx={scrollIcon} />
        </IconButton>
      )}

      {canScroll && showRightArrow && (
        <IconButton onClick={() => handleScroll("right")} sx={rightScrollButton}>
          <ArrowForwardIosIcon sx={scrollIcon} />
        </IconButton>
      )}

    <MaterialReactTable
      muiTablePaperProps={{
        sx: {
          boxShadow: "none",
          borderRadius: "5px",
        },
      }}
      muiTableContainerProps={{
        ref: tableContainerRef,
        sx: {
          maxWidth: "100%",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            height: "12px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "6px",
            "&:hover": {
              background: "#555",
            },
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#888 #f1f1f1",
        },
      }}
      muiTableHeadCellProps={({ column }) => {
        const isSelectColumn = column.id === ROW_SELECT_COLUMN_ID;
        const isLocked = isColumnOrderLocked(column);
        const canReorderColumn =
          !!setColumnOrder && !isLocked && !isSelectColumn;
        const canSort =
          column.getCanSort() && !isSelectColumn && !isLocked;

        const baseHeadCellSx = {
          position: "relative",
          backgroundColor: "#367598",
          color: "#fff",
          fontWeight: "bold",
          textAlign: "left !important",
          padding: "8px",
          "& .Mui-TableHeadCell-Content": {
            position: "relative",
            width: "100%",
          },
        };

        if (isSelectColumn) {
          return {
            sx: {
              ...baseHeadCellSx,
              cursor: "default",
              "& .Mui-TableHeadCell-Content-Actions": {
                display: "none",
              },
              "& .MuiTableSortLabel-root": {
                display: "none",
              },
              "& .Mui-TableHeadCell-Content-Labels": {
                pointerEvents: "auto",
                cursor: "default",
              },
            },
          };
        }

        return {
          sx: {
            ...baseHeadCellSx,
            cursor: canReorderColumn ? "grab" : "default",
            ...(canReorderColumn && {
              "& .Mui-TableHeadCell-Content-Actions": {
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                zIndex: 4,
                margin: 0,
                padding: 0,
              },
            }),
            "& .Mui-TableHeadCell-Content-Labels": {
              position: "relative",
              zIndex: 5,
              pointerEvents: "none",
              cursor: canReorderColumn ? "grab" : "default",
            },
            ...(canSort && {
              "& .MuiTableSortLabel-root": {
                color: "#fff !important",
                pointerEvents: "auto",
                cursor: "pointer",
                zIndex: 6,
              },
              "& .Mui-TableSortLabel-root": {
                color: "#fff !important",
                pointerEvents: "auto",
                cursor: "pointer",
                zIndex: 6,
              },
              "& .MuiTableSortLabel-root.Mui-active": {
                color: "#fff !important",
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
                opacity: column.getIsSorted() ? 1 : 0,
                transition: "opacity 150ms ease-in-out",
              },
              "&:hover .MuiTableSortLabel-icon": {
                opacity: 1,
              },
              "&:hover .Mui-TableSortLabel-icon": {
                opacity: 1,
              },
            }),
          },
        };
      }}
      muiTableHeadCellSortLabelProps={{
        onClick: (event) => {
          event.stopPropagation();
        },
      }}
      enableColumnActions = {false}
      muiTableBodyCellProps={{
        sx: {
          padding: "8px",
          color: "#333",
        },
      }}
      displayColumnDefOptions={{
        "mrt-row-select": {
          size: 10,
          enableColumnOrdering: false,
          enableSorting: false,
          enablePinning: true,
          muiTableHeadCellProps: {
            sx: {
              padding: "8px 12px",
              paddingTop: "1rem",
              backgroundColor: "#367598",
              cursor: "default",
              "& .Mui-TableHeadCell-Content-Actions": {
                display: "none",
              },
              "& .MuiTableSortLabel-root": {
                display: "none",
              },
              "& .MuiCheckbox-root": {
                color: "#fff !important",
                "&.Mui-checked": {
                  color: "#fff !important",
                },
                "& .MuiSvgIcon-root": {
                  color: "#fff !important",
                },
              },
            },
          },
          muiTableBodyCellProps: {
            sx: {
              padding: "8px 12px",
              "& .MuiCheckbox-root": {
                padding: "4px",
              },
            },
          },
        },
        actions: {
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: "#367598 !important",
              color: "#fff !important",
              fontWeight: "bold",
              textAlign: "center !important",
              padding: "8px",
              cursor: "default",
              "& .Mui-TableHeadCell-Content-Actions": {
                display: "none",
              },
              "& .MuiTableSortLabel-root": {
                display: "none",
              },
              "& .MuiIconButton-root": {
                color: "#fff !important",
              },
            },
          },
        },
      }}
      columns={columns}
      data={data}
      enableRowSelection={selectable ? true : false}
      onRowSelectionChange={setRowSelection}
      getRowId={(row) => row.id}
      enableSorting
      enableGlobalFilter={false}
      enableColumnFilters={false}
      enableDensityToggle={false}
      manualPagination={manualPagination}
      rowCount={totalData}
      onPaginationChange={onPaginationChange}
      renderRowActionMenuItems={false}
      enableFullScreenToggle={false}
      enableHiding={!!setColumnVisibility}
      enableTopToolbar={false}
      enableColumnPinning={true}
      enableColumnOrdering={!!setColumnOrder}
      muiColumnDragHandleProps={
        setColumnOrder
          ? ({ column }) => {
              if (isColumnOrderLocked(column)) {
                return { sx: { display: "none" } };
              }

              return {
                sx: {
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  minWidth: "100%",
                  minHeight: "100%",
                  margin: 0,
                  borderRadius: 0,
                  opacity: 0,
                  cursor: "grab",
                  pointerEvents: "auto",
                  "& .MuiSvgIcon-root": {
                    display: "none",
                  },
                },
              };
            }
          : undefined
      }
      state={{
        pagination,
        sorting,
        rowSelection,
        columnVisibility: columnVisibility || {},
        ...(resolvedColumnPinning && { columnPinning: resolvedColumnPinning }),
        ...(setColumnOrder && effectiveColumnOrder?.length
          ? { columnOrder: effectiveColumnOrder }
          : {}),
      }}
      initialState={{
        density: "comfort",
        ...(resolvedColumnPinning && { columnPinning: resolvedColumnPinning }),
      }}
      muiPaginationProps={{ rowsPerPageOptions: [10, 25, 50, 100] }}
      manualSorting
      onSortingChange={onSortingChange}
      onColumnVisibilityChange={setColumnVisibility || undefined}
      onColumnOrderChange={
        setColumnOrder ? handleColumnOrderChange : undefined
      }
      renderDetailPanel={
        enableCollapsibleRow
          ? ({ row }) =>
              row?.original?.campaigns?.length > 0 ? (
                <Box sx={{ backgroundColor: "#eff0ef" }}>
                  <UserCampaignStatsRow row={row} />
                </Box>
              ) : null
          : undefined
      }
    />
    </Box>
  );
};

export default DataTable;
